import React from 'react';
import { motion } from 'framer-motion';
import { HeartIcon, CompassIcon, SparklesIcon, SettingsIcon } from 'lucide-react';
import { GlowButton } from './GlowButton';
import { ParticleField } from './ParticleField';
interface ResultProps {
  onContinue: () => void;
}
const features = [{
  icon: HeartIcon,
  title: 'Cartas del deseo',
  desc: 'Provocá momentos inesperados'
}, {
  icon: CompassIcon,
  title: 'Ruleta de la pasión',
  desc: 'Dejá que el destino decida'
}, {
  icon: SparklesIcon,
  title: 'Verdad o atrevimiento',
  desc: 'Subí la tensión, turno a turno'
}, {
  icon: SettingsIcon,
  title: 'Configuración personalizada',
  desc: 'Adaptado a su estilo'
}];
export function Result({
  onContinue
}: ResultProps) {
  return <motion.div className="relative w-full min-h-screen flex flex-col px-6 py-12 overflow-hidden bg-black" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} data-id="element-60">
      {/* ────────── ATMOSPHERIC LAYERS (back to front) ────────── */}

      {/* Layer 1: Primary backdrop — shoulder touch, soft & large */}
      <motion.div initial={{
      opacity: 0,
      scale: 1.1
    }} animate={{
      opacity: 0.18,
      scale: 1
    }} transition={{
      duration: 2.5,
      ease: 'easeOut'
    }} className="absolute inset-0 z-0" style={{
      backgroundImage: `url('https://cdn.magicpatterns.com/uploads/rn3iYDaqv8WtSecisF8psX/descarga_(5).jpg')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center 30%',
      filter: 'grayscale(100%) contrast(1.15) brightness(0.85) blur(2px)'
    }} data-id="element-61" />

      {/* Layer 2: Secondary texture — whisper, top-right, very faint */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 0.08
    }} transition={{
      duration: 3,
      delay: 0.4,
      ease: 'easeOut'
    }} className="absolute -top-20 -right-20 w-[60vw] h-[60vw] max-w-[420px] max-h-[420px] z-0 rounded-full" style={{
      backgroundImage: `url('https://cdn.magicpatterns.com/uploads/kmiNARVmivQadPNC9Z6Djd/descarga_(4).jpg')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      filter: 'grayscale(100%) contrast(1.1) blur(40px)',
      maskImage: 'radial-gradient(circle at center, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 70%)',
      WebkitMaskImage: 'radial-gradient(circle at center, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 70%)'
    }} data-id="element-62" />

      {/* Layer 3: Dark vertical contrast gradient (readability floor) */}
      <div className="absolute inset-0 z-0 pointer-events-none" style={{
      background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 35%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0.92) 100%)'
    }} data-id="element-63" />

      {/* Layer 4: Side vignette for cinematic feel */}
      <div className="absolute inset-0 z-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse at center, rgba(0,0,0,0) 30%, rgba(0,0,0,0.55) 100%)'
    }} data-id="element-64" />

      {/* Layer 5: Top pink ambient glow */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 2,
      delay: 0.3
    }} className="absolute top-0 left-1/2 -translate-x-1/2 w-[90vw] max-w-[500px] h-[300px] z-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse at center top, rgba(255,45,111,0.18) 0%, rgba(255,45,111,0.05) 35%, rgba(0,0,0,0) 70%)',
      filter: 'blur(20px)'
    }} data-id="element-65" />

      {/* Layer 6: Bottom red ember glow */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[100vw] max-w-[600px] h-[250px] z-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse at center bottom, rgba(185,28,60,0.12) 0%, rgba(0,0,0,0) 65%)',
      filter: 'blur(30px)'
    }} data-id="element-66" />

      <ParticleField data-id="element-67" />

      {/* ────────── CONTENT ────────── */}
      <div className="relative z-10 max-w-md w-full mx-auto flex-1 flex flex-col justify-center space-y-8 py-8" data-id="element-68">
        {/* Result Badge */}
        <motion.div initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        delay: 0.2,
        duration: 0.5,
        type: 'spring'
      }} className="mx-auto" data-id="element-69">
          <div className="px-5 py-1.5 rounded-full bg-accent-pink/15 border border-accent-pink/40 backdrop-blur-xl shadow-[0_0_30px_rgba(255,45,111,0.25)]" data-id="element-70">
            <span className="text-xs font-semibold tracking-[0.25em] text-accent-pink uppercase" data-id="element-71">
              Tu resultado
            </span>
          </div>
        </motion.div>

        {/* Title */}
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4,
        duration: 0.6
      }} className="text-4xl md:text-5xl font-display font-bold text-center leading-[1.05] tracking-tight" style={{
        textShadow: '0 0 30px rgba(255,45,111,0.5), 0 2px 20px rgba(0,0,0,0.8)'
      }} data-id="element-72">
          CONEXIÓN INTENSA
        </motion.h1>

        {/* Subtitle */}
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6,
        duration: 0.6
      }} className="text-base text-white/75 leading-relaxed text-center max-w-sm mx-auto" style={{
        textShadow: '0 2px 12px rgba(0,0,0,0.8)'
      }} data-id="element-73">
          Ustedes tienen una conexión fuerte y disfrutan las experiencias que
          generan tensión, juego y descubrimiento.
        </motion.p>

        {/* Feature Cards */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.8,
        duration: 0.6
      }} className="space-y-3 pt-2" data-id="element-74">
          <h3 className="text-[10px] font-semibold tracking-[0.3em] uppercase text-white/45 text-center mb-4" data-id="element-75">
            Su experiencia incluye
          </h3>
          <div className="grid grid-cols-2 gap-3" data-id="element-76">
            {features.map((feature, index) => {
            const Icon = feature.icon;
            return <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 1 + index * 0.1,
              duration: 0.5
            }} whileHover={{
              scale: 1.03,
              y: -3
            }} className="relative group" data-id="element-77">
                  {/* Glow halo on hover */}
                  <div className="absolute -inset-0.5 bg-gradient-to-br from-accent-pink/50 to-accent-red/30 rounded-2xl opacity-0 group-hover:opacity-100 blur-md transition-opacity duration-500" data-id="element-78" />

                  {/* Card */}
                  <div className="relative h-full flex flex-col items-start space-y-3 p-4 rounded-2xl border border-white/[0.08] hover:border-accent-pink/40 transition-colors duration-300 overflow-hidden" style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)',
                backdropFilter: 'blur(20px) saturate(140%)',
                WebkitBackdropFilter: 'blur(20px) saturate(140%)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08), 0 8px 32px rgba(0,0,0,0.4)'
              }} data-id="element-79">
                    {/* Subtle inner gradient sheen */}
                    <div className="absolute inset-0 opacity-50 pointer-events-none" style={{
                  background: 'radial-gradient(circle at top left, rgba(255,45,111,0.08) 0%, rgba(0,0,0,0) 60%)'
                }} data-id="element-80" />

                    <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-accent-pink/30 to-accent-red/15 flex items-center justify-center border border-accent-pink/30 shadow-[0_0_20px_rgba(255,45,111,0.3)]" data-id="element-81">
                      <Icon className="w-4 h-4 text-accent-pink" data-id="element-82" />
                    </div>
                    <div className="relative space-y-1" data-id="element-83">
                      <h4 className="text-sm font-semibold text-white leading-tight" data-id="element-84">
                        {feature.title}
                      </h4>
                      <p className="text-[11px] text-white/55 leading-snug" data-id="element-85">
                        {feature.desc}
                      </p>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </motion.div>

        {/* CTA with enhanced glow */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1.5,
        duration: 0.6
      }} className="pt-4 relative" data-id="element-86">
          {/* CTA ambient halo */}
          <motion.div className="absolute inset-0 -m-4 rounded-full pointer-events-none" style={{
          background: 'radial-gradient(ellipse at center, rgba(255,45,111,0.35) 0%, rgba(255,45,111,0) 70%)',
          filter: 'blur(20px)'
        }} animate={{
          opacity: [0.6, 1, 0.6],
          scale: [1, 1.05, 1]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-87" />
          <div className="relative" data-id="element-88">
            <GlowButton onClick={onContinue} fullWidth data-id="element-89">
              COMENZAR EXPERIENCIA
            </GlowButton>
          </div>
        </motion.div>
      </div>
    </motion.div>;
}